import React, { useEffect, useState, forwardRef, useImperativeHandle, useRef } from 'react'
import { dispatchHandler, handleApiChange } from '../../../helper/apiHelperFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { getPermissionKey } from '../../../helper/permissions';
import { GenericFormSpreadTypeComponent } from '../../../genericComponents/forms/GenericFormSpreadTypeComponent';
import Icons from '../../../assets/icons';
import statesJson from '../../../constants/states.json'
import GenericCustomButton from '../../../genericComponents/GenericCustomButton';
import GenericInput from '../../../genericComponents/input/GenericInputComponent';
import { BriefcaseBusiness, Building2, CirclePlus, Contact, FileText, FileUp, HandCoins, Landmark, MapPinned, NotebookPen, Paperclip, ShieldCheck, ExternalLink, Sparkles, Stamp, Trash2, User, UserPlus, X } from 'lucide-react';
import { finalSubmitMandatoryValidationsCheck, getFileNameFromExt, getFileTypeFromUrl } from '../../../helper/helperTableFunctions';
import GenericSpinnerLoader from '../../../loaders/GenericSpinnerLoader';
import { parseJwt } from '../../../helper/genericFunction';
import LanguageTranslator from '../../../locals/LanguageTranslator';
import dayjs from 'dayjs';
import { Tooltip } from 'antd';
import ParentModal from '../../../genericComponents/ParentModal';
import GenericMediaViewModal from '../../../genericComponents/media/GenericMediaViewModal';
import FullPageLoader from '../../../loaders/FullPageLoader';
import useCustomToast from '../../../hooks/useCustomToast';

const OnboardingFormSection = forwardRef((props, ref) => {


    const { inputData = {}, setInputData, errorData = {}, setErrorData, formSettings, setFormSettings, currentStep, ownerDetailsArray, setOwnerDetailsArray, userBusinessType, keysData } = props;
    const customToast = useCustomToast();
    const getBootSettingsForApplicationData = useSelector(state => state?.generalStore?.getBootSettingsForApplicationData);
    const [panAutofill, setPanAutofill] = useState("");
    const [autofilledFields, setAutofilledFields] = useState({});
    const autofillChequeRef = useRef(null);

    const authStore = useSelector(state => state?.authStore);
    const vendorOnboardingGetVendorDetailData = authStore?.vendorOnboardingGetVendorDetailData;

    const handlePanSubmit = () => {
        const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
        if (!panAutofill || !panRegex.test(panAutofill)) {
            setErrorData({
                ...errorData,
                panAutofill: ["Please provide a valid 10-digit PAN number (e.g., ABCDE1234F)."]
            });
            return;
        }
        dispatchHandler(dispatch, 'vendorOnboardingGetVendorDetailRequest', { panNo: panAutofill }, { "X-Scope-Id": "common.commonfunctionality.view" })
    }

    useEffect(() => {
        if (vendorOnboardingGetVendorDetailData?.isSuccess) {
            const responseData = vendorOnboardingGetVendorDetailData?.data;
            const data = responseData?.resource || responseData; 
            setInputData(prev => {
                const updatedData = { ...prev };
                Object.keys(keysData).forEach(sectionKey => {
                    const sectionFields = keysData[sectionKey];
                    if (!updatedData[sectionKey]) updatedData[sectionKey] = {};
                    sectionFields.forEach(fieldName => {
                        if (data.hasOwnProperty(fieldName) && data[fieldName] !== null && data[fieldName] !== "") {
                            updatedData[sectionKey][fieldName] = data[fieldName];
                        }
                    });
                });
                return updatedData;
            });
            setAutofilledFields(prev => {
                const filledKeys = { ...prev };
                Object.keys(keysData).forEach(sectionKey => {
                    const sectionFields = keysData[sectionKey];
                    sectionFields.forEach(fieldName => {
                        if (data.hasOwnProperty(fieldName) && data[fieldName] !== null && data[fieldName] !== "") {
                            filledKeys[fieldName] = true;
                        }
                    });
                });
                return filledKeys;
            });
            dispatchHandler(dispatch, 'vendorOnboardingGetVendorDetailClearRequest');
        }
    }, [vendorOnboardingGetVendorDetailData?.isSuccess]);

    const renderPanAutofillSection = () => {

        if (currentStep?.key !== "Company & Contact Details" || props?.viewMode) return null;

        return (
            <div className='flex items-center justify-between p-[10px] m-[20px] border border-[#DAE4EE] rounded-[5px] bg-white'>
                <div className='flex items-center gap-2'>
                    <div className='w-10 h-10 flex items-center justify-center'>
                        <Sparkles size={16} className='text-[#67768E]' />
                    </div>
                    <div>
                        <h4 className='!m-0 text-[12px] font-semibold whitespace-nowrap'>Autofill Details by PAN Card</h4>
                    </div>
                </div>
                <div className='flex items-end gap-2'>
                    <div className='flex flex-col gap-1'>
                        <GenericInput
                            inputKey="panAutofill"
                            placeholder="Enter PAN No."
                            type="text"
                            value={panAutofill}
                            onChange={(e) => {
                                return setPanAutofill(e?.target?.value?.toUpperCase() || "")
                            }}
                            errorData={errorData}
                            setErrorData={setErrorData}
                            validationFunction={(val) => {
                                if (!val) return [];
                                const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
                                if (!panRegex.test(val)) {
                                    return ["Please provide a valid 10-digit PAN number (e.g., ABCDE1234F)."];
                                }
                                return [];
                            }}
                            dependentFieldsObject={[panAutofill]}
                            clearPressFunction={() => {
                                setPanAutofill("");
                                const updatedErrorData = { ...errorData };
                                delete updatedErrorData["panAutofill"];
                                setErrorData(updatedErrorData);
                            }}
                            isDisabled={props?.viewMode || false}
                            genInputOuterClassName="!mb-0"
                            genInputExtraClass="!w-[237px] !h-[32px] !border-[#DAE4EE] !mb-0"
                        />
                    </div>
                    <GenericCustomButton
                        label="Submit"
                        type="primary"
                        onClick={handlePanSubmit}
                        disabled={props?.viewMode || false}
                        className="!h-[32px] !font-semibold !text-[12px]"
                    />
                </div>
            </div>
        )
    }


    const dispatch = useDispatch();

    const parseTokenData = parseJwt(sessionStorage.getItem('token'));

    const allStates = statesJson?.["states"];
    let selectedState;

    const mandatoryKey = `isMandatory${"Vendor"}`
    const displayKey = `isDisplay${"Vendor"}`
    const isEditableKey = `${"isEditable"}${"Vendor"}`
    const getPermissionObj = getPermissionKey("common", "commonFunctionality", "upload");

    const getDigicatFormSettingsData = useSelector(state => state?.administrationStore?.getDigicatFormSettingsData);
    const getDropdownData = useSelector(state => state?.generalStore?.getDropdownData)

    const handleGetLocation = () => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setInputData({
                        ...inputData,
                        company: {
                            ...inputData?.company,
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        }
                    })
                    let errorDataUpdate = {...errorData};
                    if(errorDataUpdate?.company?.longitude || errorDataUpdate?.company?.latitude) {
                        delete errorDataUpdate?.company?.longitude
                        delete errorDataUpdate?.company?.latitude
                        setErrorData(errorDataUpdate);
                    }
                },
                (err) => {
                    console.error(err);
                }
            );
        } else {
            console.error("Geolocation is not supported by this browser.");
        }
    };
    
    // Example function inside child
    const checkError = () => {
        let inputState = inputData;
        let errors = {};
        let inputSections = getInputSections();
        Object.keys(inputSections)?.map(inputKey => {
            let data = inputSections?.[inputKey];
            let inputFields = data?.inputFields();
            let errorMessages = {};
            if (inputFields?.length > 0) {
                errorMessages = finalSubmitMandatoryValidationsCheck(inputFields, inputState?.[inputKey], "label");
            }

            if (Object.keys(errorMessages || {})?.length > 0) {
                errors[inputKey] = errorMessages;
            }
        })
        setErrorData(errors);
        return Object.keys(errors || {})?.length > 0;
    };

    // Expose function to parent
    useImperativeHandle(ref, () => ({
        errorFunction: checkError,
    }));


    const getFormSubType = {
        "Business Registration & Compliances": "Business Registration & Compliance",
        "Category Specific Details": "Category-Specific Details",
    };

    const [dropdownData, setDropdownData] = useState({});
    const [selectedModal, setSelectedModal] = useState("");
    const [selectedData, setSelectedData] = useState("")

    useEffect(() => {
        const dropdownTypes = [
            "COMPANY_TYPE",
            "PAYMENT_TERMS"
        ];
        dropdownTypes.forEach((type, index) => {
            setTimeout(() => {
                handleApiChange(dispatch, "getDropdownRequest", { type }, getPermissionObj);
            }, (index + 1) * 300);
        });
        // handleApiChange(dispatch, "getDropdownRequest", {
        //     type: ''
        // }, getPermissionObj)
    }, []);

    useEffect(() => {
        if (getDropdownData?.isSuccess) {
            let keysMapping = {
                "COMPANY_TYPE": "companyType",
                "PAYMENT_TERMS": "paymentTerms"
            }
            setDropdownData({
                ...dropdownData,
                [keysMapping?.[getDropdownData?.data?.type]]: getDropdownData?.["data"]?.resource || [],
            });
        }
    }, [getDropdownData?.isSuccess])

    useEffect(() => {
        if (currentStep?.key) {
            let payload = {
                type: "onboarding",
                formSubType: getFormSubType?.[currentStep?.key] ?? currentStep?.key,
                customDomain: window.location?.hostname // window.location?.hostname?.includes("prod.supplymint.com") ? undefined : "window.location?.hostname"
            };
            dispatchHandler(dispatch, "getDigicatFormSettingsRequest", payload, getPermissionObj);
        }
    }, [currentStep?.key]);

    // Handle the response of the form settings api
    useEffect(() => {
        if (getDigicatFormSettingsData?.isSuccess) {
            let data = getDigicatFormSettingsData?.data?.resource || [];
            setFormSettings(data);
        }
    }, [getDigicatFormSettingsData?.isSuccess]);

    // Function : To check if the input field is mandate or not
    const isInputFieldMandatory = (input) => {
        switch (input?.supplymintKey) {
            default: return input?.[mandatoryKey] == 1;
        }
    }

    // Function : To check if the input field is disabled or not
    const isInputFieldDisabled = (input, formType) => {
        if(props?.viewMode) {
            // if(input?.supplymintKey == "paymentTerms") return false
            return true;
        }

        // if (autofilledFields[input?.supplymintKey]) {
        //     return true;
        // }

        switch (input?.supplymintKey) {
            case "latitude": case "longitude": return true;
            // case "bankName":
            // case "accountNumber":
            // case "ifscCode":
            // case "micrCode":
            // case "cancelledChequeUpload": return true;
            case "state": return !inputData?.[formType]?.country;
            case "city": return !inputData?.[formType]?.state;
            case "pincode": return !inputData?.[formType]?.city;
            default: return input?.[isEditableKey] != 1;
        }
    }

    // Fucntion : To provide the right icon
    const provideRightIcon = (key) => {
        switch (key) {
            case "vendorSku":
                if (formSettings?.[key]?.inputType?.toUpperCase() == "LOV") {
                    return <Icons.GlobalSearchIcon />
                } else {
                    return null;
                }
            case "latitude": case "longitude": {
                return <Tooltip overlayClassName={`!p-0`} overlayStyle={{ fontSize: '12px' }} title='Use current location'><MapPinned strokeWidth={1.5} size={18} onClick={props?.viewMode ? null : handleGetLocation} className="hover:opacity-75 cursor-pointer" /></Tooltip>
            }
            default: {
                if (formSettings?.[key]?.inputType?.toUpperCase() == "LOV" || key?.inputType?.toUpperCase() == "LOV") {
                    return <Icons.DownArrowNew />
                }
                else {
                    return null;
                }
            }
        }
    }

    // Function : To check if the input field is mandate or not
    const isInputFieldDisplay = (input) => {
        switch (input?.supplymintKey) {
            default: return input?.[displayKey] == 1;
        }
    }

    // Function : To handle the input changes
    const handleInputChange = (key, values) => {
        let { data, updatedInputState } = values || {};
        const getFieldValue = (fieldKey) => {
            const fieldValue = updatedInputState?.[fieldKey];
            if (fieldValue && typeof fieldValue === "object" && !Array.isArray(fieldValue)) {
                return fieldValue?.[fieldKey] ?? "";
            }
            return fieldValue ?? "";
        };
        const previousCountry = inputData?.company?.country?.country ?? inputData?.company?.country ?? "";
        const previousState = inputData?.company?.state?.state ?? inputData?.company?.state ?? "";
        const previousCity = inputData?.company?.city?.city ?? inputData?.company?.city ?? "";

        switch (key) {
            case "country": {
                const currentCountry = data?.country ?? getFieldValue("country");
                if (!currentCountry || (previousCountry && previousCountry !== currentCountry)) {
                    delete updatedInputState?.state
                    delete updatedInputState?.city
                    delete updatedInputState?.pincode
                }
                break;
            }
            case "state": {
                const currentState = data?.state ?? getFieldValue("state");
                if (!currentState || (previousState && previousState !== currentState)) {
                    delete updatedInputState?.city
                    delete updatedInputState?.pincode
                }
                break;
            }
            case "city": {
                const currentCity = data?.city ?? getFieldValue("city");
                if (!currentCity || (previousCity && previousCity !== currentCity)) {
                    delete updatedInputState?.pincode
                }
                break;
            }
            case "yearEstablished": {
                updatedInputState.yearEstablished = data ? data?.year() : ""
                break;
            }
        }
        return updatedInputState;
    }

    // Function : To get the header keys of the data
    const getHeaderKeys = (key, input) => {
        switch (key) {
            case "country": case "state": case "city": case "companyType": case "Cloud Capabilities":
            case "vendorSku": {
                if (["collection"]?.includes(key)) {
                    return {
                        multiSelectCol: {
                            value: "",
                        },
                        [key]: { value: formSettings?.[key]?.displayName || "" }
                    }
                }
                return input?.isMultiSelect ? {
                    multiSelectCol: {
                        value: "",
                    },
                    [input?.supplymintKey]: { value: input?.supplymintKey || "" }

                } :
                    {
                        [input?.supplymintKey]: { value: input?.supplymintKey || "" }
                    }

            }
            default: {
                return input?.isMultiSelect ? {
                    multiSelectCol: {
                        value: "",
                    },
                    [input?.supplymintKey]: { value: input?.supplymintKey || "" }

                } :
                    {
                        [input?.supplymintKey]: { value: input?.supplymintKey || "" }
                    }
            }
        }
    }

    // Function :  to get the item keys of the data
    const getItemKeys = (key) => {
        switch (key) {
            case "country": case "state": case "city": case "companyType": case "Cloud Capabilities": {
                return {
                    [key]: [key],
                }
            }
            default: {
                return {
                    [key]: [key],
                }
            }
        }
    }

    // Function : To provide the input props for all the input fields
    const provideInputProps = (input, type) => {

        let commonPayload = {
            search: {
                placeholder: `Search ${input?.["displayName"]}`,
            },
            headerKeys: getHeaderKeys(input?.supplymintKey, input),
            itemKeys: getItemKeys(input?.supplymintKey),
        }

        selectedState = inputData?.[type]?.["state"]?.["state"] || ""
        switch (input?.inputType) {
            case "UPLOAD": case "LOV_UPLOAD": {
                return {
                    maxSize: 10,
                    multipleFileUpload: ['Regulatory Compliance Certificates', 'Sustainability Certificates', "Portfolio or Client References", "Software Licensing & Certifications File", "Food Safety Certificates", "EHS Compliance Policy", "Electrical Safety or Energy Compliance"].includes(input?.supplymintKey) ? true : false,
                    fileUploadActionName: "finalSubmit",
                    fileKeys: ["fileName", "", "url"],
                    getListData: (data) => data?.resource,
                    api: {
                        reduxState: "digivendStore",
                        apiName: "uploadUtilityDocument",
                        apiPayload: (files, removedFiles) => {
                            let formData = new FormData();
                            Object.values(files)?.map(file => {
                                formData.append("file", file);
                            })

                            formData.append('documentId', parseTokenData?.jti || 0);
                            formData.append('type', `ONBOARDING_${input?.supplymintKey?.toUpperCase().replace(/\s+/g, "_")}`);
                            formData.append('requestId', '');
                            return formData;
                        },
                        apiExtraHeaders: getPermissionKey("common", "commonFunctionality", "upload")
                    },
                    renderCustomPlaceholder: (fileName) => {
                        const isSpecialMatch = [
                            'Aadhar Upload',
                            'primaryContactAadhaarUpload',
                            'certificateOfIncorporationUpload',
                            'Business Registration Certificate',
                            'Environmental, Health & Safety',
                            'Demo / Portfolio File',
                            'SLAs & Support Details File'
                        ].includes(input?.supplymintKey);
                    
                        const handleFileClick = (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const file = inputData?.[type]?.[input?.supplymintKey]?.[0];
                            handleChange('viewMediaModal', file);
                        };
                    
                        if (fileName) {
                            return isSpecialMatch ? (
                                <div className='gibu-inner w-full text-center flex flex-col items-center justify-center bg-[#FBFBFB]'>
                                    <FileText className='mb-3 shrink-0' size={40} color='#707494' />
                                    <LanguageTranslator tag='h4' className='text-[13px] font-normal'>Drag & Drop a document file, or <span className='font-medium text-[#2969FF]'>Browse</span></LanguageTranslator>
                                    <LanguageTranslator tag="p" className='mb-0 text-[11px] font-normal text-[#67768E]'>Max file size : 10MB | Supported Format : PDF, JPEG, PNG</LanguageTranslator>
                                    <div className="uploaded-file-name flex justify-center mt-2">
                                        <span 
                                            onClick={handleFileClick} 
                                            className="uembri-uploaded-text cursor-pointer hover:opacity-80"
                                        >
                                            {fileName}
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <div onClick={handleFileClick} className='flex items-center gap-2 cursor-pointer'>
                                     <span className="uembri-uploaded-text !m-0">{fileName}</span>
                                </div>
                            );
                        }
                    
                        return isSpecialMatch ? (
                            <div className={`gibu-inner w-full text-center flex flex-col items-center justify-center bg-[#FBFBFB] ${props?.viewMode ? 'opacity-50' : ''}`}>
                                <FileText className='mb-3 shrink-0' size={40} color='#707494' />
                                <LanguageTranslator tag='h4' className='text-[13px] font-normal'>Drag & Drop a document file, or <span className='font-medium text-[#2969FF]'>Browse</span></LanguageTranslator>
                                <LanguageTranslator tag="p" className='mb-0 text-[11px] font-normal text-[#67768E]'>Max file size : 10MB | Supported Format : PDF, JPEG, PNG</LanguageTranslator>
                            </div>
                        ) : (
                            <LanguageTranslator tag='span' className={`gibu-text flex items-center gap-1.5 ${props?.viewMode ? 'opacity-50' : ''}`}>
                                <Icons.ProcChooseFileIcon /> Upload File
                            </LanguageTranslator>
                        );
                    },
                    hideDefaultUploadTag: true, 
                }
            }
            case "radioInput": {
                return {
                    getListData: () => ([
                        { key: "TRUE", value: "Yes" },
                        { key: "FALSE", value: "No" }
                    ]),
                    customChecked: () => <div className='h-[12px] w-[12px] rounded-full shrink-0 border-4 border-primary'></div>,
                    customUnchecked: () => <div className='h-[12px] w-[12px] rounded-full shrink-0 border-2 border-borderSlateGrey'></div>
                }
            }
            default: {
                switch (input?.supplymintKey) {
                    case "country": {
                        return {
                            ...commonPayload,
                            search: {
                                placeholder: `Search ${input?.["displayName"]}`,
                                customSearchFunction: (listState, listFunctions) => {
                                    const { listData, currentSearch } = listState;
                                    return listData?.filter(item => item?.[input?.supplymintKey]?.toLowerCase().includes(currentSearch?.toLowerCase()));
                                }
                            },
                            getListData: () => ([{ "country": "India" }]),
                            renderCustomItems: {
                                0: () => {
                                    return (
                                        <div className='custome-value-input p-lr-15 p-top-10'>
                                            <input autoComplete="off"
                                                className='cvi-input'
                                                placeholder='Type Custom Value'
                                                value={inputData?.company?.[input?.supplymintKey]?.[input?.supplymintKey]}
                                                onChange={e => handleChange(input?.supplymintKey, e?.target?.value ?? "")}
                                            />
                                        </div>
                                    )
                                }
                            }
                        }
                    }
                    case "state": {
                        return {
                            ...commonPayload,
                            search: {
                                placeholder: `Search ${input?.["displayName"]}`,
                                customSearchFunction: (listState, listFunctions) => {
                                    const { listData, currentSearch } = listState;
                                    return listData?.filter(item => item?.[input?.supplymintKey]?.toLowerCase().includes(currentSearch?.toLowerCase()));
                                }
                            },
                            getListData: () => Object.keys(allStates || {})?.map(val => ({ [input?.supplymintKey]: val })),
                            renderCustomItems: {
                                0: () => {
                                    return (
                                        <div className='custome-value-input p-lr-15 p-top-10'>
                                            <input autoComplete="off"
                                                className='cvi-input'
                                                placeholder='Type Custom Value'
                                                value={inputData?.company?.[input?.supplymintKey]?.[input?.supplymintKey]}
                                                onChange={e => handleChange(input?.supplymintKey, e?.target?.value ?? "")}
                                            />
                                        </div>
                                    )
                                }
                            }
                        }
                    }
                    case "city": {
                        return {
                            ...commonPayload,
                            search: {
                                placeholder: `Search ${input?.["displayName"]}`,
                                customSearchFunction: (listState, listFunctions) => {
                                    const { listData, currentSearch } = listState;
                                    return listData?.filter(item => item?.[input?.supplymintKey]?.toLowerCase().includes(currentSearch?.toLowerCase()));
                                }
                            },
                            getListData: () => allStates?.[selectedState]?.map(val => ({ [input?.supplymintKey]: val })),
                            renderCustomItems: {
                                0: () => {
                                    return (
                                        <div className='custome-value-input p-lr-15 p-top-10'>
                                            <input autoComplete="off"
                                                className='cvi-input'
                                                placeholder='Type Custom Value'
                                                value={inputData?.company?.[input?.supplymintKey]?.[input?.supplymintKey]}
                                                onChange={e => handleChange(input?.supplymintKey, e?.target?.value ?? "")}
                                            />
                                        </div>
                                    )
                                }
                            }
                        }
                    }
                    case "companyType": {
                        return {
                            ...commonPayload,
                            search: false,
                            getListData: () => dropdownData?.[input?.supplymintKey]?.map(item => ({ [input?.supplymintKey]: item })),
                        }
                    }
                    case "paymentTerms": {
                        return {
                            ...commonPayload,
                            search: false,
                            getListData: () => dropdownData?.[input?.supplymintKey]?.map(item => ({ [input?.supplymintKey]: item })),
                        }
                    }
                    case "Cloud Capabilities": {
                        return {
                            ...commonPayload,
                            viewDataType: "table-view",
                            modalMainClass: "use-as-generic-dropdown",
                            search: false,
                            multiselect: input?.isMultiSelect ? {
                                selectId: [input?.supplymintKey],
                            } : false,
                            headerKeys: input?.isMultiSelect ? {
                                multiSelectCol: {
                                    value: "",
                                },
                                [input?.supplymintKey]: { value: input?.supplymintKey || "" }

                            } : {
                                [input?.supplymintKey]: { value: input?.supplymintKey || "" }
                            },
                            itemKeys: {
                                [input?.supplymintKey]: [input?.supplymintKey],
                            },
                            getListData: () => (input?.options)?.map(item => ({ [input?.supplymintKey]: item })),
                        }
                    }
                    case "productType": {
                        return {
                            headerKeys: {
                                "productType": {}
                            },
                            itemKeys: {
                                "productType": ["productType"]
                            },
                            getListData: () => {
                                return ["Sampling", "Production", "Ready Stock", "Other"]?.map(item => ({
                                    "productType": item,
                                }))
                            }
                        }
                    }
                    case "Aadhar Upload": case "gstUpload": case "panUpload": case "msmeUpload": case "vatUpload": case "exciseUpload": case "cancelledChequeUpload": case "certificateOfIncorporationUpload": {
                        return {
                            maxSize: 10,
                            fileUploadActionName: "finalSubmit",
                            fileKeys: ["oldFileName", "", "url"],
                            getListData: (data) => data?.resource,
                            api: {
                                reduxState: "digivendStore",
                                apiName: "uploadUtilityDocument",
                                apiPayload: (files, removedFiles) => {
                                    let formData = new FormData();
                                    Object.values(files)?.map(file => {
                                        formData.append("file", file);
                                    })

                                    formData.append('documentId', parseTokenData?.jti || 0);
                                    formData.append('type', `ONBOARDING_${input?.supplymintKey?.toUpperCase().replace(/\s+/g, "_")}`);
                                    formData.append('requestId', '');
                                    return formData;
                                },
                                apiExtraHeaders: getPermissionKey("common", "commonFunctionality", "upload")
                            },
                        }
                    }
                    case "NDA Signed": {
                        return {
                            ...commonPayload,
                            search: false,
                            viewDataType: "table-view",
                            modalMainClass: "use-as-generic-dropdown",
                            getListData: () => [{ [input?.supplymintKey]: "Yes" }, { [input?.supplymintKey]: "No" }, { [input?.supplymintKey]: "Will Sign During Onboarding" }],
                        }
                    }
                    case "Tax Invoice Capability": {
                        return {
                            ...commonPayload,
                            search: false,
                            viewDataType: "table-view",
                            modalMainClass: "use-as-generic-dropdown",
                            getListData: () => [{ [input?.supplymintKey]: "Yes" }, { [input?.supplymintKey]: "No" }],
                        }
                    }
                    default: {
                        return {
                            headerKeys: input?.isMultiSelect ? {
                                multiSelectCol: {
                                    value: "",
                                },
                                [input?.supplymintKey]: { value: input?.supplymintKey || "" }

                            } :
                                {
                                    [input?.supplymintKey]: { value: input?.supplymintKey || "" }
                                },
                            itemKeys: {
                                [input?.supplymintKey]: [input?.supplymintKey],
                            },
                            viewDataType: "table-view",
                            modalMainClass: "use-as-generic-dropdown",
                            multiselect: input?.isMultiSelect ? {
                                selectId: [input?.supplymintKey],
                            } : false,
                            getListData: () => (input?.options)?.map(item => ({ [input?.supplymintKey]: item })),
                        };
                    }
                }
            }
        }

    }

    const changeType = {
        pincode: "pincode",
        yearEstablished: "year",
        primaryContactPhone: "mobileNo",
        primaryContactEmail: "email",
        companyAddress: "textarea",
        businessDescription: "textarea",
        primaryContactAadhaarNumber: "number",
        "Machinery Details": "textarea"
    }

    const getInputType = (key) => {
        if (changeType?.[key]) {
            return changeType?.[key];
        }

        if (formSettings?.[key]?.inputType == "LOV") {
            return "dropdown";
        }

        if (formSettings?.[key]?.inputType == "NUMBER") {
            return "number";
        }
        if (formSettings?.[key]?.inputType == "DECIMAL") {
            return "decimal";
        }

        if (formSettings?.[key]?.inputType == "MOBILENO") {
            return "mobileNo";
        }

        return formSettings?.[key]?.inputType?.toLowerCase() || "text";
    }

    const getOtherInputType = (type) => {
        if(type?.supplymintKey == "Machinery Details") {
            return "textarea";
        }
        if (type?.supplymintKey == "Phone Number") {
            return "mobileNo";
        }
        if (type?.supplymintKey == "Email") {
            return "email";
        }
        if (type?.supplymintKey == "Aadhar Number") {
            return "number";
        }
        if (type?.inputType == "LOV") {
            return "dropdown";
        }

        if (type?.inputType == "NUMBER") {
            return "number";
        }
        if (type?.inputType == "DECIMAL") {
            return "decimal";
        }

        if (type?.inputType == "MOBILENO") {
            return "mobileNo";
        }

        if (type?.inputType == "UPLOAD" || type?.inputType==="LOV_UPLOAD") {
            return "upload";
        }

        if (type?.inputType == "EMAIL") {
            return "email";
        }
        if (type?.inputType == "radioInput") {
            return "radioInput";
        }

        return "text";
    }

    const handleUploadChange = (headerKey, type, labelKey, data, mainKey) => {

        let updatedErrorData = { ...errorData }
        switch (type) {
            default:
                setInputData(prevData => {
                    let newData = { ...prevData };
                    newData[type] = {
                        ...newData[type],
                        [`${labelKey}${mainKey}${headerKey}`]: data
                    };
                    delete updatedErrorData?.[type]?.[`${labelKey}${mainKey}${headerKey}`];
                    return newData;
                });
                setErrorData(updatedErrorData)
                break;
        }
    }

    // function for special case for document upload to remove the selected card
    const handleRemoveCard = (type, sectionKey, mainKey) => {
        let updatedInputData = { ...inputData };
        delete updatedInputData?.[type]?.[sectionKey]?.[mainKey];
        delete updatedInputData?.[type]?.[`${sectionKey}${mainKey}file`];
        setInputData(updatedInputData);
    };

    const renderInputBottomComponent = (type, key) => {
        let allKeys = Object?.keys(inputData?.[type]?.[key?.supplymintKey] || {});
        let data = allKeys.filter(item => !(item.endsWith('file') && allKeys.includes(item.slice(0, -4))));
        let value = inputData?.[type] || "";
        return (
            <div className={`mt-[8px] pb-1 pr-2 w-full flex items-start gap-4 overflow-auto ${props?.viewMode ? 'opacity-50 cursor-not-allowed pointer-events-none' : ''}`}>
                {data?.map((item) =>
                    <div className="vobrss-box shrink-0 w-[170px] border border-[#C7D3DF] rounded-[3px]">
                        <div className="vobrssb-top px-2.5 py-2 border-b border-[#C7D3DF] flex items-center justify-between gap-3">
                            <div className="flex items-center gap-2">
                                <Icons.CompliancesIcon height={31} className="shrink-0" />
                                <h2 className='!m-0 !text-sm !font-bold !text-[#116FFF]'>{item}</h2>
                            </div>
                            <GenericCustomButton
                                leftIcon={<Icons.CrossIcon height={14} />}
                                onClick={() => handleRemoveCard(type, key?.supplymintKey, item)}
                                className='hover:opacity-75'
                            />
                        </div>
                        {/* <div className="vobrssb-mid">
                            <GenericInput
                                label='Valid Through'
                                type='date'
                            mandatory={field?.isMandatory}
                            value={value?.date ? dayjs(value?.date) : dayjs() || null}
                            onChange={(date) => handleUploadChange('date', inputField?.headerKey, field?.labelKey, key, date?.format("YYYY-MM-DD"))}
                            />
                        </div> */}
                        <div className="vobrssb-bot">
                            <GenericInput
                                key={`${key?.supplymintKey}${item}file`}
                                inputKey={`${key?.supplymintKey}${item}file`}
                                // label='Upload Document'
                                // mandatory={true}
                                dependentFieldsObject={[value?.[`${key?.supplymintKey}${item}file`]]}
                                value={(!Array.isArray(value?.[`${key?.supplymintKey}${item}file`]) || value?.[`${key?.supplymintKey}${item}file`]?.length == 0) ? 'Attach File' : `${+value?.[`${key?.supplymintKey}${item}file`]?.length} Files`}
                                actualInputValue={value?.[`${key?.supplymintKey}${item}file`] || []}
                                type="upload"
                                inputProps={() => ({
                                    renderCustomPlaceholder: () => {
                                        return (<div className='gibu-sel px-2.5 w-full flex items-center gap-1.5 hover:opacity-75'>
                                            {value?.[`${key?.supplymintKey}${item}file`]?.length ? <p className='mb-0 truncate max-w-[116px]'>{value?.[`${key?.supplymintKey}${item}file`]?.[0]?.oldFileName}</p> : <><CirclePlus size={19} className='shrink-0' />Add Document</>}
                                        </div>
                                        )
                                    },
                                    // deleteImgFromServer: deleteImgFromServer,
                                    maxSize: 10,
                                    fileUploadActionName: "finalSubmit",
                                    fileKeys: ["oldFileName", "", "url"],
                                    getListData: (data) => data?.resource,
                                    api: {
                                        reduxState: "digivendStore",
                                        apiName: "uploadUtilityDocument",
                                        apiPayload: (files, removedFiles) => {
                                            let formData = new FormData();
                                            Object.values(files)?.map(file => {
                                                formData.append("file", file);
                                            })

                                            formData.append('documentId', parseTokenData?.jti || 0);
                                            formData.append('type', `ONBOARDING_${item?.toUpperCase().replace(/\s+/g, "_")}`);
                                            formData.append('requestId', '');
                                            return formData;
                                        },
                                        apiExtraHeaders: getPermissionKey("common", "commonFunctionality", "upload")
                                    },
                                })}
                                onChange={(data) => handleUploadChange('file', type, key?.supplymintKey, data, item)}
                            />
                        </div>
                    </div>)}
            </div>
        )
    }

    const getInputClass = (key) => {
        switch (key) {
            case "companyName":
                return "!w-[59%]"
            case "companyType":
            case "yearEstablished":
                return "!w-[19%]"

            // textarea
            case "companyAddress":
            case "businessDescription":
            case "Machinery Details":
                return "!w-full textarea-height-100"

            // 1 row -> 4 inputs
            case "country":
            case "state":
            case "city":
            case "pincode":
                return "!w-[24%]"

            // 1 row -> 2 inputs
            case "latitude":
            case "longitude":
            case "panNumber":
            case "panUpload":
            case "gstNumber":
            case "gstUpload":
            case "vatNumber":
            case "vatUpload":
            case "exciseNumber":
            case "exciseUpload":
            case "msmeNumber":
            case "msmeUpload":
            case "bankName":
            case "accountNumber":
            case "ifscCode":
            case "micrCode":
            case "cancelledChequeUpload":
            case "paymentTerms":
            case "NDA Signed": case "Tax Invoice Capability": case "Cybersecurity Compliance": case "Cybersecurity Compliance File": case "Data Privacy Policy Available": case "Data Privacy Policy Available File":
            case "BEE Star Rating":
            case "BEE Star Rating File":
            case "Cybersecurity Audit Report":
            case "Data Privacy Policy Document":
            case "Environmental Policy Document":
            case "Factory Registration Certificate":
            case "Product Recall SOP":
            case "Barcoding & Labeling Compliance":
                return "!w-[49%]"

            // 1 row -> 1 input (full width)
            case "website":
            case "productionCapacity":
            case "Name":
            case "primaryContactName":
            case "Aadhar Number":
            case "Designation":
            case "businessRegistrationNumber":
            case "Certifications": case "Electrical Safety Compliance": case "Food Safety & Hygiene Certifications": case "Factory Audits": case "Sustainability Certifications":
            case "Regulatory Compliance":
            case "primaryContactDesignation":
            case "primaryContactAadhaarNumber":
            case "Demo / Portfolio":
            case "NDA Document":
            case "Regulatory Compliance Certificates":
            case "Sustainability Certificates":
            case "SLAs & Support Details":
            case "Return/Reject Handling Policy Document":
            case "Portfolio or Client References":
            case "Pricing Sheet or MOQ Document":
            case "Software Licensing & Certifications":
            case "Software Licensing & Certifications File":
            case "Food Safety Certificates":
            case "Sample Development Documentation":
            case "EHS Compliance Policy":
            case "Electrical Safety or Energy Compliance":
                return "!w-full"

            case "Email":
            case "primaryContactEmail":
                return "!w-[calc(100%-2%-200px)]"

            // upload input
            case "Aadhar Upload":
            case "primaryContactAadhaarUpload":
            case "certificateOfIncorporationUpload":
            case "Business Registration Certificate":
            case "Environmental, Health & Safety":
            case "Demo / Portfolio File":
            case "SLAs & Support Details File":
                return "generic-upload-component gfb-upload !w-full"


            default: return "";
        }
    }

    const disabledDate = (key, data) => {
        switch (key) {
            case "yearEstablished": {
                return data && data?.year() > dayjs()?.year();
            }
            default: return null
        }
    }

    const renderTermsConditionInput = () => {
        const termsSetting = formSettings?.["termsConditionForVendor"] || formSettings?.["termNConditionsAccepted"];
        const isChecked = Boolean(inputData?.["businessRegistrationComplaince"]?.["termsConditionForVendor"] || inputData?.["businessRegistrationComplaince"]?.["termNConditionsAccepted"]);
        const isMandatory = isInputFieldMandatory(termsSetting);
        const hasError = Boolean(errorData?.["businessRegistrationComplaince"]?.["termsConditionForVendor"]?.length || errorData?.["businessRegistrationComplaince"]?.["termNConditionsAccepted"]?.length);
        const parsedAdditionalConfig = typeof formSettings?.additionalConfig === 'string'
            ? JSON.parse(formSettings?.additionalConfig || "{}")
            : (formSettings?.additionalConfig || {});

        const termsUrl = parsedAdditionalConfig?.termsConditionForVendor ||
            formSettings?.additionalConfig?.termsConditionForVendor ||
            getBootSettingsForApplicationData?.data?.resource?.additionalConfig?.termsConditionForVendor ||
            termsSetting?.inputValue ||
            termsSetting?.additional || "";

        const handleReadTerms = (e) => {
            e?.preventDefault();
            e?.stopPropagation();
            if (termsUrl) {
                let extName = getFileTypeFromUrl(termsUrl);
                let detectedType = getFileNameFromExt(extName);
                let finalType = (detectedType && detectedType !== 'file') ? detectedType : 'pdf';
                handleChange('viewMediaModal', { url: termsUrl, fileName: 'Terms_and_Conditions', fileType: finalType });
            } else {
                customToast({
                    title: "Terms & Conditions",
                    description: "Terms & Conditions document has not been uploaded yet.",
                    status: "info"
                });
            }
        };

        const handleCheckboxToggle = (checked) => {
            if (props?.viewMode || props?.termsAlreadySaved) return;
            const newVal = checked ? 1 : 0;
            setInputData(prev => ({
                ...prev,
                businessRegistrationComplaince: {
                    ...(prev?.businessRegistrationComplaince || {}),
                    termsConditionForVendor: newVal,
                    termNConditionsAccepted: newVal
                }
            }));
            if (checked && (errorData?.["businessRegistrationComplaince"]?.["termsConditionForVendor"] || errorData?.["businessRegistrationComplaince"]?.["termNConditionsAccepted"])) {
                setErrorData(prev => {
                    const nextErrors = { ...prev };
                    if (nextErrors?.businessRegistrationComplaince) {
                        const nextBRC = { ...nextErrors.businessRegistrationComplaince };
                        delete nextBRC.termsConditionForVendor;
                        delete nextBRC.termNConditionsAccepted;
                        if (Object.keys(nextBRC).length === 0) {
                            delete nextErrors.businessRegistrationComplaince;
                        } else {
                            nextErrors.businessRegistrationComplaince = nextBRC;
                        }
                    }
                    return nextErrors;
                });
            }
        };

        const isTermsLocked = props?.viewMode || props?.termsAlreadySaved;

        return (
            <div className={`w-full rounded-[6px] border transition-all duration-200 mt-2 overflow-hidden ${
                hasError ? 'border-red-400 bg-red-50/30' : isChecked ? 'border-green-400/60 bg-green-50/30' : 'border-[#DAE4EE] bg-[#FBFBFB]'
            }`}>
                {/* Main checkbox area */}
                <div className="p-3.5">
                    <div className="flex items-start">
                        <label className={`generic-checkbox ${isTermsLocked ? 'cursor-not-allowed' : 'cursor-pointer'} !m-0 !mt-0.5 flex-shrink-0`}>
                            <input
                                type="checkbox"
                                checked={isChecked}
                                disabled={isTermsLocked}
                                onChange={(e) => handleCheckboxToggle(e.target.checked)}
                                className={isTermsLocked ? "cursor-not-allowed" : "cursor-pointer"}
                            />
                            <span className="checkmark1"></span>
                        </label>
                        <div className="flex-1">
                            <div className="flex items-center flex-wrap gap-1 text-[13px] text-[#1F2937]">
                                <LanguageTranslator tag="span">I have read and agree to the</LanguageTranslator>
                                <button
                                    type="button"
                                    onClick={handleReadTerms}
                                    className="inline-flex items-center gap-1 font-semibold text-primary hover:underline focus:outline-none cursor-pointer"
                                >
                                    <span>{termsSetting?.displayName || "Terms & Conditions"}</span>
                                    <FileText size={14} className="text-primary inline" />
                                </button>
                                {isMandatory && <span className="text-red-500 font-semibold">*</span>}
                            </div>
                            {hasError && (
                                <p className="text-red-500 text-[11px] mt-1.5 font-medium mb-0">
                                    {errorData?.["businessRegistrationComplaince"]?.["termsConditionForVendor"]?.[0] || errorData?.["businessRegistrationComplaince"]?.["termNConditionsAccepted"]?.[0] || "Please accept the Terms & Conditions to proceed."}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer: T&C link + accepted badge */}
                <div className={`flex items-center justify-between gap-3 px-3.5 py-2 border-t ${
                    isChecked ? 'border-green-200/80 bg-green-50/40' : 'border-[#EEF2F6] bg-[#F6F8FA]'
                }`}>
                    {termsUrl ? (
                        <span
                            onClick={handleReadTerms}
                            className="text-[11px] text-primary hover:underline cursor-pointer inline-flex items-center gap-1.5 font-medium"
                        >
                            <ExternalLink size={12} />
                            <LanguageTranslator tag="span">View / Read Terms & Conditions</LanguageTranslator>
                        </span>
                    ) : <span />}
                    {props?.termsAlreadySaved && isChecked && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-green-600 font-semibold">
                            <ShieldCheck size={13} />
                            <LanguageTranslator tag="span">Accepted</LanguageTranslator>
                        </span>
                    )}
                </div>
            </div>
        );
    };

    // Function : To provide the input fields 
    const getInputFields = (x) => {
        let type = x;
        let keys = [];
        switch (type) {
            case "company": {
                keys = ["companyName", "companyType", "yearEstablished", "companyAddress", "country", "state", "city", "pincode", "latitude", "longitude", "website", "businessDescription", "productionCapacity"];
                break;
            }
            case "primaryContactDetails": {
                keys = ["primaryContactName", "primaryContactPhone", "primaryContactEmail", "primaryContactDesignation", "primaryContactAadhaarNumber", "primaryContactAadhaarUpload"];
                break;
            }
            case "businessRegistrationComplaince": {
                keys = ["termsConditionForVendor", "businessRegistrationNumber", "panNumber", "panUpload", "gstNumber", "gstUpload", "vatNumber", "vatUpload", "exciseNumber", "exciseUpload", "msmeNumber", "msmeUpload", "certificateOfIncorporationUpload"];
                break;
            }
            case "financialDetails": {
                keys = ["bankName", "accountNumber", "ifscCode", "micrCode", "cancelledChequeUpload", "paymentTerms"];
                break;
            }
            case "categorySpecificDetails": case "applicableAcrossAllIndustries": case "industrySpecificCompliance": case "documentUploadSection":
            case "environment": case "social": case "governance": case "esgCertifications": {
                const getKeyAndIndex = {
                    "categorySpecificDetails": 0,
                    "categorySpecificDetailsKey": "categorySpecificDetails",
                    "applicableAcrossAllIndustries": 0,
                    "applicableAcrossAllIndustriesKey": "businessCertificationsCompliance",
                    "industrySpecificCompliance": 1,
                    "industrySpecificComplianceKey": "businessCertificationsCompliance",
                    "documentUploadSection": 0,
                    "documentUploadSectionKey": "businessDocumentUpload",
                    "environment": 0,
                    "social": 1,
                    "governance": 2,
                    "environmentKey": "esgParticulars",
                    "socialKey": "esgParticulars",
                    "governanceKey": "esgParticulars",
                    "esgCertifications": 0,
                    "esgCertificationsKey": "esgCertifications"
                };
                let allTypeDocument= JSON.parse(formSettings?.[getKeyAndIndex?.[`${type}Key`]]?.additional || "{}")?.categories
                const categoryDocument= allTypeDocument?.find(item => item?.category?.toLowerCase() === userBusinessType?.toLowerCase())
                let inputFields = type==="documentUploadSection" || type==="industrySpecificCompliance" || type == "categorySpecificDetails"  ? categoryDocument : JSON.parse(formSettings?.[getKeyAndIndex?.[`${type}Key`]]?.additional || "{}")?.categories?.[getKeyAndIndex[type]] || {};
                let inputs = inputFields?.fields?.map(key => {
                    // console.log('getOtherInputType(key)', key, getOtherInputType(key), inputData?.[type]?.[key?.supplymintKey], Object.keys(inputData?.[type]?.[key?.supplymintKey] || {}))
                    return ({
                        key: key?.supplymintKey,
                        genInputExtraClass: `!mr-[1%] !mb-[1%] ${getOtherInputType(key) === 'upload' ? 'upload-input' : ''} ${getOtherInputType(key) === 'textarea' ? 'textarea-w-scroll' : ''} ${getOtherInputType(key) === 'radioInput' ? 'customRadioInput' : ''} ${(type == "categorySpecificDetails" || type == "environment" || type == "social" || type == "governance" || type == "esgCertifications") ? "!w-full" : getInputClass(key?.supplymintKey)}`,
                        iconRight: provideRightIcon(key),
                        isDisabled: props?.viewMode,
                        // isDisabled: () => isInputFieldDisabled(formSettings?.[key]),
                        mandatory: () => key.isMandatory === '1',
                        label: key?.displayName,
                        type: getOtherInputType(key),
                        onChange: (data, state) => handleInputChange(key?.supplymintKey, {
                            data: data,
                            updatedInputState: state,
                        }),
                        // renderCutomInput: (props?.viewMode && inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName) ([
                        //     'Aadhar Upload',
                        //     'primaryContactAadhaarUpload',
                        //     'certificateOfIncorporationUpload',
                        //     'Business Registration Certificate',
                        //     'Environmental, Health & Safety',
                        //     'Demo / Portfolio File',
                        //     'SLAs & Support Details File'
                        // ].includes(key?.supplymintKey)) ? 
                        // ()=> 
                        //     <div className='gibu-inner p-[25px] w-full text-center flex flex-col items-center justify-center bg-[#FBFBFB]'>
                        //         <FileText className='mb-3 shrink-0' size={40} color='#707494' />
                        //         <div onClick={()=>handleChange('viewMediaModal', inputData?.[type]?.[key?.supplymintKey]?.[0])} className='px-3 py-0.5 text-[11px] font-medium text-[#2969FF] underline rounded-full truncate max-w-[300px] cursor-pointer hover:opacity-75'>{inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName}</div>
                        //     </div>
                        //     : null,
                        renderCutomInput:
                        (getOtherInputType(key) === 'upload' && props?.viewMode && inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName) ? () => ([
                            'Aadhar Upload',
                            'primaryContactAadhaarUpload',
                            'certificateOfIncorporationUpload',
                            'Business Registration Certificate',
                            'Environmental, Health & Safety',
                            'Demo / Portfolio File',
                            'SLAs & Support Details File',
                            'Digital Sign-off Agreement Upload'
                        ].includes(key?.supplymintKey)) ?
                        <div className='gibu-inner p-[25px] w-full text-center flex flex-col items-center justify-center bg-[#FBFBFB]'>
                            <FileText className='mb-3 shrink-0' size={40} color='#707494' />
                            <div onClick={()=>handleChange('viewMediaModal', inputData?.[type]?.[key?.supplymintKey]?.[0])} className='px-3 py-0.5 text-[11px] font-medium text-[#2969FF] underline rounded-full truncate max-w-[300px] cursor-pointer hover:opacity-75'>{inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName}</div>
                        </div>
                        : <div className='gibu-inner px-3 py-1 h-[32px] w-full text-center flex items-center gap-1.5 bg-[#F5F5F5] rounded-[3px]'>
                            <FileText className='shrink-0' size={16} color='#AEAEB0' />
                            <div onClick={()=>handleChange('viewMediaModal', inputData?.[type]?.[key?.supplymintKey]?.[0])} className='text-[11px] font-medium text-[#2969FF] underline rounded-full truncate max-w-[300px] cursor-pointer hover:opacity-75'>{inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName}</div>
                        </div>
                        : (getOtherInputType(key) === 'dropdown' && props?.viewMode && Object.keys(inputData?.[type]?.[key?.supplymintKey] || {})?.length) ?
                            () => <div className='gibu-inner px-3 py-1 min-h-[32px] w-full text-center flex items-center gap-1.5 flex-wrap bg-[#F5F5F5] rounded-[3px] opacity-50 cursor-not-allowed'>
                                {Object.keys(inputData?.[type]?.[key?.supplymintKey] || {})?.map((item, index) => (
                                    <span className='text-xs font-semibold text-[#23235f]'>{item}{index < Object.keys(inputData?.[type]?.[key?.supplymintKey] || {})?.length-1 ? ', ' : ''}</span>
                                ))}
                            </div>
                        : null,
                        inputProps: () => provideInputProps(key, type),
                        customInputValue: ['Regulatory Compliance Certificates', 'Sustainability Certificates', "Portfolio or Client References", "Software Licensing & Certifications File", "Food Safety Certificates", "EHS Compliance Policy", "Electrical Safety or Energy Compliance"].includes(key?.supplymintKey) ? () => {
                            const fileList = inputData?.[type]?.[key?.supplymintKey];
                            if (!fileList || fileList?.length === 0) return false;
                            console.log('fileList', key?.supplymintKey, fileList)
                            // if(props?.viewMode) {
                            //     return fileList?.map(item => (
                            //         <span className='uembri-uploaded-text !m-0' onClick={()=>handleChange('viewMediaModal', item)}>{item?.fileName}</span>
                            //     ))
                            // }
                            return `${fileList.length} ${fileList.length === 1 ? "File" : "Files"}`;

                        } : false,
                        inputComponents: {
                            bottom: Object?.keys(inputData?.[type]?.[key?.supplymintKey] || {})?.length && key?.isMultiSelect == "1" && getInputClass(key?.supplymintKey) ? () => renderInputBottomComponent(type, key) : null
                        },
                        validationFunction: (val) => validationsCheck(key?.supplymintKey, val)
                    })
                });

                return inputs;
            }
            default: {
                if (type.startsWith("ownerDetails")) {
                    let inputFields = JSON.parse(formSettings?.ownerDetails?.additional || "{}");
                    let inputs = inputFields?.fields?.map(key => {
                        return ({
                            key: key?.supplymintKey,
                            // iconRight: provideRightIcon(key),
                            // isDisabled: () => isInputFieldDisabled(formSettings?.[key]),
                            isDisabled: props?.viewMode,
                            genInputExtraClass: `!mr-[1%] !mb-[1%] ${getOtherInputType(key) === 'upload' ? 'upload-input' : ''} ${getInputClass(key?.supplymintKey)}`,
                            label: key?.displayName,
                            type: getOtherInputType(key),
                            onChange: (data, state) => handleInputChange(key?.supplymintKey, {
                                data: data,
                                updatedInputState: state,
                            }),
                            // renderCutomInput: (props?.viewMode && inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName) && ([
                            //     'Aadhar Upload',
                            //     'primaryContactAadhaarUpload',
                            //     'certificateOfIncorporationUpload',
                            //     'Business Registration Certificate',
                            //     'Environmental, Health & Safety',
                            //     'Demo / Portfolio File',
                            //     'SLAs & Support Details File'
                            // ].includes(key?.supplymintKey)) ? 
                            // ()=> 
                            //     <div className='gibu-inner p-[25px] w-full text-center flex flex-col items-center justify-center bg-[#FBFBFB]'>
                            //         <FileText className='mb-3 shrink-0' size={40} color='#707494' />
                            //         <div onClick={()=>handleChange('viewMediaModal', inputData?.[type]?.[key?.supplymintKey]?.[0])} className='px-3 py-0.5 text-[11px] font-medium text-[#2969FF] underline rounded-full truncate max-w-[300px] cursor-pointer hover:opacity-75'>{inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName}</div>
                            //     </div>
                            //  : null,
                            renderCutomInput:
                            (getOtherInputType(key) === 'upload' && props?.viewMode && inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName) ? () => ([
                                'Aadhar Upload',
                                'primaryContactAadhaarUpload',
                                'certificateOfIncorporationUpload',
                                'Business Registration Certificate',
                                'Environmental, Health & Safety',
                                'Demo / Portfolio File',
                                'SLAs & Support Details File'
                            ].includes(key?.supplymintKey)) ? 
                            <div className='gibu-inner p-[25px] w-full text-center flex flex-col items-center justify-center bg-[#FBFBFB]'>
                                <FileText className='mb-3 shrink-0' size={40} color='#707494' />
                                <div onClick={()=>handleChange('viewMediaModal', inputData?.[type]?.[key?.supplymintKey]?.[0])} className='px-3 py-0.5 text-[11px] font-medium text-[#2969FF] underline rounded-full truncate max-w-[300px] cursor-pointer hover:opacity-75'>{inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName}</div>
                            </div>
                            : <div className='gibu-inner px-3 py-1 h-[32px] w-full text-center flex items-center gap-1.5 bg-[#F5F5F5] rounded-[3px]'>
                                <FileText className='shrink-0' size={16} color='#AEAEB0' />
                                <div onClick={()=>handleChange('viewMediaModal', inputData?.[type]?.[key?.supplymintKey]?.[0])} className='text-[11px] font-medium text-[#2969FF] underline rounded-full truncate max-w-[300px] cursor-pointer hover:opacity-75'>{inputData?.[type]?.[key?.supplymintKey]?.[0]?.fileName}</div>
                            </div>
                            : null,
                            inputProps: () => provideInputProps(key, type),
                            validationFunction: (val) => validationsCheck(key, val)
                            // genInputExtraClass: "gfb-upload",
                        })
                    });

                    return inputs;
                }
            }
        }

        const getDisplayName = (key) => {
            let input = formSettings?.[key] || {};
            switch (key) {
                default: return input?.["displayName"] || ""
            }
        }

        let inputs = keys?.map(key => {
            let input = formSettings?.[key] || {};
            // let restProps = getRestInputProps(key);
            // IF the input field display key is on then only show the input field.
            if (isInputFieldDisplay(input)) {
                if (key === "termsConditionForVendor" || key === "termNConditionsAccepted") {
                    return ({
                        key: key,
                        genInputExtraClass: '!w-full !mr-0 !mb-2',
                        isDisabled: () => props?.viewMode,
                        mandatory: () => isInputFieldMandatory(formSettings?.[key] || formSettings?.["termsConditionForVendor"] || formSettings?.["termNConditionsAccepted"]),
                        avoidMandatoryCheck: () => true,
                        label: "",
                        type: "checkbox",
                        renderCutomInput: () => renderTermsConditionInput(),
                        validationFunction: (val) => validationsCheck(key, val),
                    });
                }
                return ({
                    key: key,
                    // isDisabled: () => isInputFieldEditable(input),
                    iconRight: provideRightIcon(key),
                    genInputExtraClass: `!mr-[1%] !mb-[1%] ${getInputType(key) === 'upload' ? 'upload-input' : ''} ${getInputType(key) === 'textarea' ? 'textarea-w-scroll' : ''} ${getInputClass(key)}`,
                    showTooltipLenght: getInputType(key) === "email" ? 51 : undefined,
                    genericTooltipClassName: getInputType(key) === "email" ? '!transform-none !left-[50px] after:!left-[20%]' : '',
                    isDisabled: () => isInputFieldDisabled(formSettings?.[key], type),
                    mandatory: () => isInputFieldMandatory(formSettings?.[key]),
                    label: getDisplayName(key), //<LanguageTranslator tag="label" className="gib-label-heading"><LanguageTranslator>{getDisplayName(key)}</LanguageTranslator> {isInputFieldMandatory(formSettings?.[key]) && <span className="mandatory">*</span>}</LanguageTranslator>,
                    type: getInputType(key),
                    onChange: (data, state) => handleInputChange(key, {
                        data: data,
                        updatedInputState: state,
                    }),
                    renderCutomInput: 
                       (getInputType(key) === 'upload' && props?.viewMode && inputData?.[type]?.[key]?.[0]?.fileName) ? () => ([
                        'Aadhar Upload',
                        'primaryContactAadhaarUpload',
                        'certificateOfIncorporationUpload',
                        'Business Registration Certificate',
                        'Environmental, Health & Safety',
                        'Demo / Portfolio File',
                        'SLAs & Support Details File',
                        'Digital Sign-off Agreement Upload'
                    ].includes(key)) ? 
                     <div className='gibu-inner p-[25px] w-full text-center flex flex-col items-center justify-center bg-[#FBFBFB]'>
                        <FileText className='mb-3 shrink-0' size={40} color='#707494' />
                        <div onClick={()=>handleChange('viewMediaModal', inputData?.[type]?.[key]?.[0])} className='px-3 py-0.5 text-[11px] font-medium text-[#2969FF] underline rounded-full truncate max-w-[300px] cursor-pointer hover:opacity-75'>{inputData?.[type]?.[key]?.[0]?.fileName}</div>
                    </div>
                    : <div className='gibu-inner px-3 py-1 h-[32px] w-full text-center flex items-center gap-1.5 bg-[#F5F5F5] rounded-[3px]'>
                        <FileText className='shrink-0' size={16} color='#AEAEB0' />
                        <div onClick={()=>handleChange('viewMediaModal', inputData?.[type]?.[key]?.[0])} className='text-[11px] font-medium text-[#2969FF] underline rounded-full truncate max-w-[300px] cursor-pointer hover:opacity-75'>{inputData?.[type]?.[key]?.[0]?.fileName}</div>
                    </div>
                    : null,
                    inputProps: () => provideInputProps(input, type),
                    validationFunction: (val) => validationsCheck(key, val),
                    disabledDate: (current) => disabledDate(key, current),
                    // ...restProps,
                })
            }
        })?.filter(item => Boolean(item));

        return inputs;
    }

    const validationsCheck = (type, val) => {
        switch (type) {
            case "termsConditionForVendor": case "termNConditionsAccepted": return ((isInputFieldMandatory(formSettings?.["termsConditionForVendor"]) || isInputFieldMandatory(formSettings?.["termNConditionsAccepted"])) && !val) ? ["Please accept the Terms & Conditions to proceed."] : null;
            case "latitude": return val ? !/^(-?[1-8]?\d(\.\d+)?|90(\.0+)?)$/.test(val) ? [`Please enter valid latitude`] : null : null
            case "longitude": return val ? !/^(-?(1[0-7]\d|\d{1,2})(\.\d+)?|180(\.0+)?)$/.test(val) ? [`Please enter valid longitude`] : null : null
            case "gstNumber": return val ? !/^[0-9]{2}[A-Z]{4}[1-9A-Z]{1}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(val) ? ["Please enter the correct 15 digit GST Number"] : null : null
            case "panNumber": return val ? !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(val) ? ["Please enter the correct 10 digit PAN Number"] : null : null
            case "accountNumber": return val ? !/^[a-zA-Z0-9]{9,34}$/.test(val) ? ["Please enter valid Account Number"] : null : null
            case "ifscCode": return val ? !/(^[A-Z]{4}0[A-Z0-9]{6}$)|(^[A-Z]{4}[A-Z]{2}[A-Z0-9]{2}([A-Z0-9]{3})?$)/.test(val) ? ["Please enter valid IFSC / SWIFT Code"] : null : null
            case "micrCode": return val ? !/^[0-9]{9}$/.test(val) ? ["Please enter valid MICR Code"] : null : null
            case "vatNumber": return val ? !/^[0-9]{11,15}$/.test(val) ? ["Please enter valid VAT Number"] : null : null
            case "businessRegistrationNumber": return val ? !/^[LU][0-9]{5}[A-Z]{2}[0-9]{4}[A-Z]{3}[0-9]{6}$/.test(val) ? ["Please enter valid Business Registration Number"] : null : null
            case "exciseNumber": return val ? !/^[A-Z]{5}[0-9]{4}[A-Z]{1}X[M|D][0-9]{3}$/.test(val) ? ["Please enter valid Excise Number"] : null : null
            case "msmeNumber": return val ? !/^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/.test(val) ? ["Please enter valid MSME Number"] : null : null
            case "primaryContactAadhaarNumber": case "Aadhar Number": return val ? !/^[2-9]{1}[0-9]{11}$/.test(val) ? ["Please enter valid Aadhar Number"] : null : null
            case "What percentage of your raw materials come from sustainable sources & recycled?": case "What are the percentages of non-biodegradable, compostible, recyclable, and hazardous waste?": case "What percentage of water comes from recycled or harvested sources?":
            case "What percentage of employees have health insurance?": case "What percentage of employees have accident insurance?": case "What percentage of employees receive paternity benefits?": case "What percentage of employees are eligible for maternity benefits?":
            case "What percentage have access to company-provided daycare?": case "What percentage receive retirement benefits?": case "What percentage of affected workers have been re-employed?": case "Gender Diversity in Workforce": case "Do you use any disclaimer for safe use of your product or in disposal or recycle?":
            case "Do you have any internal assessment for sustainability paramerter or as per SEDEX 4 pillar?": case "What proportion of materials come from smaller producers?": case "What portion of your materials meet your internal standards?": case "What percentage of affected workers have been re-employed?What percentage receive retirement benefits?":
            case "What percentage of employees receive paternity benefits?": return val ? !/^(100(\.0+)?|([0-9]{1,2})(\.[0-9]+)?)$/.test(val) ? [`Please enter valid ${type}`] : null : null
        }
    }

    const getSectionHeading = (heading) => {
        const icon = {
            'Company': <Building2 size={16} color='#67768E' />,
            'Contact & Ownership': <Contact size={16} color='#67768E' />,
            'Primary Contact Details': <User size={16} color='#67768E' />,
            'Business Registration & Compliance': <NotebookPen size={16} color='#67768E' />,
            'Financial Details': <HandCoins size={16} color='#67768E' />,
            'Business Details(Hardware)': <BriefcaseBusiness size={16} color='#67768E' />,
            'Certifications & Compliance':  <BriefcaseBusiness size={16} color='#67768E' />,
            'Applicable Across All Industries': <Icons.Certifications />,
            'Industry-Specific Compliance': <Icons.IndustrySearch />,
            'Document Upload Section': <Icons.DocumentUpload />,
            'Environment': <Icons.Environment />,
            'Social': <Icons.Social />,
            'Governance': <Landmark size={16} color='#67768E' />,
            'ESG Certifications & Documentation': <Stamp size={16} color='#67768E' />,
        }

        return <>
            {icon[heading] && <span className='relative top-[3px] mr-1.5 inline-block'>{icon[heading]}</span>}
            {heading}
        </>
    }

    const handleChange = (type, data) => {
        switch (type) {
            case "addOwnerDetails": {
                let ownerDetailsArrayUpdate = [...ownerDetailsArray];
                ownerDetailsArrayUpdate.push(`ownerDetails${ownerDetailsArray.length}`)
                setOwnerDetailsArray(ownerDetailsArrayUpdate);
            }
            break;

            case "removeOwnerDetails": {
                let ownerDetailsArrayUpdate = [...ownerDetailsArray], updatedinputData = {...inputData};
                let index = ownerDetailsArrayUpdate?.indexOf(data);
                if(index !== -1) {
                    ownerDetailsArrayUpdate.splice(index, 1);
                }
                if(updatedinputData?.[data]) {
                    delete updatedinputData?.[data];
                    setInputData(updatedinputData);
                }
                setOwnerDetailsArray(ownerDetailsArrayUpdate);
            }
            break;

            case "viewMediaModal": {
                let extName = getFileTypeFromUrl(data?.url);
                let fileType = data?.fileType || getFileNameFromExt(extName) || 'pdf';
                const fileData = [{
                    fileUrl: data?.url,
                    fileType: fileType,
                    fileName: data?.fileName || 'Document.pdf'
                }]
                setSelectedData(fileData)
                setSelectedModal(type)
            }
            break;

            case "country": case "state": case "city": {
                let formDataUpdate = {...inputData};
                formDataUpdate.company = {
                    ...formDataUpdate?.company,
                    [type]: {
                        [type]: data
                    }
                }
                setInputData(formDataUpdate);
                setErrorData({});
                break;
            }
            
            default:
                break;
        }
    }

    const buildInputSections = () => {
        const baseSections = {
            company: {
                label: getSectionHeading("Company"),
                inputFields: () => getInputFields("company"),
            },
            primaryContactDetails: {
                label: getSectionHeading("Primary Contact Details"),
                inputFields: () => getInputFields("primaryContactDetails"),
            },
        };

        // dynamically add ownerDetails sections
        const ownerSections = ownerDetailsArray.reduce((acc, ownerKey, index) => {
            acc[ownerKey] = {
                label: () => (
                    <div className="flex items-center justify-between pr-[20px]">
                        <div>{getSectionHeading(index == 0 ? formSettings?.["ownerDetails"]?.displayName || "Owner Details" : `${formSettings?.["ownerDetails"]?.displayName}(${index + 1})`)}</div>
                        {props?.viewMode ? null : ownerKey == "ownerDetails" ? <GenericCustomButton
                            type="default"
                            leftIcon={<UserPlus size={16} className='shrink-0' />}
                            label="Add"
                            onClick={() => handleChange(`addOwnerDetails`)}
                        /> : <GenericCustomButton
                            type="rejectOutline"
                            leftIcon={<Icons.DeleteIcon height={16} />}
                            onClick={() => handleChange(`removeOwnerDetails`, ownerKey)}
                        />}
                    </div>
                ),
                inputFields: () => getInputFields(ownerKey),
            };
            return acc;
        }, {});

        return {
            ...baseSections,
            ...ownerSections,
        };
    };

    // usage
    const companyAndContactDetails = buildInputSections();
    console.log(JSON.parse(formSettings?.businessCertificationsCompliance?.additional || "{}")?.categories?.[1]?.category)

    // Function : To provide the input sections
    const getInputSections = () => {
        let inputSections = {};
        switch (currentStep?.key) {
            case "Company & Contact Details": {
                inputSections = companyAndContactDetails;
            }
                break;
            case "Business Registration & Compliances": {
                inputSections = {
                    "businessRegistrationComplaince": {
                        label: getSectionHeading("Business Registration & Compliance"),
                        inputFields: () => getInputFields("businessRegistrationComplaince"),
                    },
                    "financialDetails": {
                        label: () => (
                        <div className='flex items-center justify-between w-full pr-[20px]'>
                            <div>{getSectionHeading("Financial Details")}</div>
                            <div className='flex items-center gap-2'>
                                {!props?.viewMode && <div className='flex items-center gap-2'>
                                    <Sparkles size={14} className='text-[#67768E]' />
                                <span className='text-[12px] font-semibold text-[#1F2937] whitespace-nowrap'>Autofill by Cancelled Cheque</span>
                            </div>}
                            {!props?.viewMode && <GenericInput
                                type="upload"
                                inputProps={() => ({
                                    maxSize: 10,
                                    fileUploadActionName: "finalSubmit",
                                    fileKeys: ["oldFileName", "", "url"],
                                    getListData: (data) => data, 
                                    api:{
                                        reduxState: "digivendStore",
                                        apiName: "uploadUtilityDocument",
                                        apiPayload: (files)=>{
                                            let formData= new FormData();
                                            const fileToUpload = Array.isArray(files) ? files[0] : Object.values(files)[0];
                                            if (fileToUpload) {
                                                formData.append("file", fileToUpload);
                                            }
                                            formData.append('documentId', parseTokenData?.jti || 0);
                                            formData.append('type', 'ONBOARDING_CANCEL_CHEQUE'); 
                                            formData.append('requestId', '');
                                            return formData;
                                        },
                                        apiExtraHeaders: getPermissionKey("common", "commonFunctionality", "upload")
                                    },
                                    renderCustomPlaceholder: () => (
                                        <div className='flex items-center gap-2 px-3 py-2 rounded-[5px] cursor-pointer' style={{backgroundColor:' var(--primary-color)'}}>
                                            <span className='text-[12px] text-white'>Upload File</span>
                                            <FileUp size={15} className='text-[#FFFFFF]' />
                                        </div>
                                    )
                                })}
                                onChange={(data, value) => {
                                    const fileResponse = data?.resource ;
                                    const requestId = data?.requestId;
                                    if (requestId) {
                                        setInputData(prev=>({
                                            ...prev,
                                            financialDetails:{
                                                ...(prev.financialDetails || {}),
                                                cancelledChequeUpload: fileResponse
                                            }
                                        }))
                                        dispatchHandler(dispatch, 'vendorOnboardingGetVendorDetailRequest', { requestId }, { "X-Scope-Id": "common.commonfunctionality.view" })
                                        dispatchHandler(dispatch, 'uploadUtilityDocumentClearRequest');
                                    }
                                }}
                                inputRef={autofillChequeRef}
                                genInputOuterClassName="!mb-0"
                                gibInnerClassName="remove_dash_border"
                                genInputExtraClass="!mb-0"
                            />}
                            </div>
                    </div>
                ),
                inputFields: () => getInputFields("financialDetails"),
            },
                }
                break;
            }
            case "Category Specific Details": {
                const certCategories = JSON.parse(formSettings?.categorySpecificDetails?.additional || "{}")?.categories || [];
                const matchedCertCategory = certCategories?.find(
                    item => item?.category?.toLowerCase() === userBusinessType?.toLowerCase()
                );
                inputSections = {
                    "categorySpecificDetails": {
                        label: getSectionHeading(`Business Details(${matchedCertCategory?.category})`),
                        inputFields: () => getInputFields("categorySpecificDetails"),
                    }
                }
                break;
            }
            case "Certifications & Compliance": {
                const certCategories = JSON.parse(formSettings?.businessCertificationsCompliance?.additional || "{}")?.categories;
                const matchedCertCategory = certCategories?.find(
                    item => item?.category?.toLowerCase() === userBusinessType?.toLowerCase()
                );
                inputSections = {
                    "applicableAcrossAllIndustries": {
                        label: getSectionHeading(matchedCertCategory?.category),
                        inputFields: () => getInputFields("applicableAcrossAllIndustries"),
                    },
                    "industrySpecificCompliance": {
                        inputFields: () => getInputFields("industrySpecificCompliance"),
                    }
                }
                break;
            }
            case "Document Upload Section": {
                inputSections = {
                    "documentUploadSection": {
                        label: getSectionHeading(JSON.parse(formSettings?.businessDocumentUpload?.additional || "{}")?.heading || "Document Upload"),
                        inputFields: () => getInputFields("documentUploadSection"),
                    }
                }
                break;
            }
            case "ESG Particulars": {
                inputSections = {
                    "environment": {
                        label: getSectionHeading(JSON.parse(formSettings?.esgParticulars?.additional || "{}")?.categories?.[0]?.category || ""),
                        inputFields: () => getInputFields("environment"),
                    },
                    "social": {
                        label: getSectionHeading(JSON.parse(formSettings?.esgParticulars?.additional || "{}")?.categories?.[1]?.category || ""),
                        inputFields: () => getInputFields("social"),
                    },
                    "governance": {
                        label: getSectionHeading(JSON.parse(formSettings?.esgParticulars?.additional || "{}")?.categories?.[2]?.category || ""),
                        inputFields: () => getInputFields("governance"),
                    }
                }
                break;
            }
            case "ESG Certifications & Documentation": {
                inputSections = {
                    "esgCertifications": {
                        label: getSectionHeading(JSON.parse(formSettings?.esgCertifications?.additional || "{}")?.heading || ""),
                        inputFields: () => getInputFields("esgCertifications"),
                    }
                }
                break;
            }
        }


        return inputSections;
    }
    
    const getParentChildProps = () => {
        let payload = {
            closeModal: () => { setSelectedModal(false) },
            animationType: "bottom",
        }
        switch (selectedModal) {
            case "viewMediaModal": {
                return {
                    ...payload,
                    animationType: 'bottom',
                    parentModalOuterMostClassName: 'flex items-center justify-center',
                    postitionProps: { position: 'relative' },
                    dimensions: { width: "100vw", height: "100vh", backgroundColor: 'transparent' },
                    filesData: selectedData
                }
            }
            default : 
        }
    }

    const getChildComponent = () => {
        switch (selectedModal) {
            case "viewMediaModal": {
                return GenericMediaViewModal;
            }
            default : 
        }
    }

    // Function : To render the image upload sections
    const renderInputSections = () => {

        let payload = {
            inputSections: getInputSections(),
            inputState: inputData,
            updateInputState: setInputData,
            errorState: errorData,
            updateErrorState: setErrorData,
            hideAllButtons: true,
            renderCustomItems: {
               "0": renderPanAutofillSection
            }
        }

        const renderForm = () => {
            return <GenericFormSpreadTypeComponent
                {...payload}
            />
        }

        if (getDigicatFormSettingsData?.isLoading) {
            return (
                <div className='h-full w-full flex items-center justify-center'>
                    <GenericSpinnerLoader />
                </div>
            )
        }

        return (
            <div className='cprb-left pl-1 pt-[14px] pb-[4px] h-full'>
                {renderForm()}
            </div>
        )
    }

    return (
        <>
            <div className='w-full h-full max-w-[700px] rounded-[9px] border border-[#E1E1E1] bg-white'>
                {renderInputSections()}
            </div>
            {vendorOnboardingGetVendorDetailData?.isLoading && <FullPageLoader />}
            {selectedModal && <ParentModal
                getParentChildProps={getParentChildProps()}
                childComponent={getChildComponent()}
                closeModal={() => { setSelectedModal(false) }}
            />}
        </>
    )
});

export default OnboardingFormSection